import { produce } from 'immer';

import { RESET_STATE } from 'store/global/actions';

import { TPO_CERTIFICATION_ACTION_TYPE } from '../actions';

const initialState = {
  // The sub-company whose cert/pageInfo/history reads are the latest requested. A response for any
  // other sub-company is stale (the user navigated away while it was in flight) and is dropped.
  requestedSubCompanyId: null,
  // The sub-company whose cert/pageInfo/history the slice currently holds, so a consumer can tell
  // another sub-company's data from its own instead of rendering it under the wrong route.
  ownerSubCompanyId: null,
  // Flat TpoCertificationDto from getActive (header binds directly to this).
  cert: null,
  // Flat TpoCertificationPageDto (pageStatus / certifiedUntil / cycleType) — the action unwraps
  // getActivePage's `{ activeCertification }` envelope so consumers read one canonical shape.
  pageInfo: null,
  profile: null,
  // Provenance for the signup config the cert cycle ran under ({ tpoSignupConfigurationId,
  // name, isDefault }). Null when unresolvable.
  signupConfig: null,
  // TpoStepEvaluationResponseDto — the server-evaluated steps this cycle ran, which drive which
  // review sections and questionnaire steps the tab renders. Null when unresolvable.
  evaluatedSteps: null,
  // The certification whose step evaluation has settled (resolved or failed). It lets the tab tell
  // an evaluation still in flight for the current cert apart from one that came back empty.
  evaluatedStepsCertificationId: null,
  // The certification of the latest evaluation requested. A response for any other certification
  // is stale (the cert changed while it was in flight) and is dropped, so a late reply can never
  // overwrite the current cert's evaluation.
  evaluatedStepsRequestedCertificationId: null,
  // TpoDocumentDto[] uploaded against the certification. Null means "not read yet, or the read
  // failed"; an empty array means the cycle has no uploads.
  documents: null,
  history: [],
  canStartNewCycle: null,
};

const checkIsSettledForRequestedSubCompany = (draft, meta) =>
  (meta?.subCompanyId ?? null) === draft.requestedSubCompanyId;

const tpoCertificationReducer = (state = initialState, { type, payload, meta }) =>
  produce(state, draft => {
    switch (type) {
      case TPO_CERTIFICATION_ACTION_TYPE.GET_SUB_COMPANY_READ_REQUEST: {
        const requestedSubCompanyId = meta?.subCompanyId ?? null;

        if (requestedSubCompanyId !== draft.requestedSubCompanyId) {
          return { ...initialState, requestedSubCompanyId };
        }

        break;
      }

      case TPO_CERTIFICATION_ACTION_TYPE.GET_ACTIVE:
        if (!checkIsSettledForRequestedSubCompany(draft, meta)) {
          break;
        }

        draft.cert = payload || null;
        draft.ownerSubCompanyId = meta?.subCompanyId ?? null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.GET_ACTIVE_PAGE:
        if (!checkIsSettledForRequestedSubCompany(draft, meta)) {
          break;
        }

        draft.pageInfo = payload || null;
        draft.ownerSubCompanyId = meta?.subCompanyId ?? null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.GET_SIGNUP_CONFIG:
        draft.signupConfig = payload || null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.GET_EVALUATED_STEPS_REQUEST:
        draft.evaluatedStepsRequestedCertificationId = meta?.certificationId ?? null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.GET_EVALUATED_STEPS:
        if ((meta?.certificationId ?? null) !== draft.evaluatedStepsRequestedCertificationId) {
          break;
        }

        draft.evaluatedSteps = payload || null;
        draft.evaluatedStepsCertificationId = meta?.certificationId ?? null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.GET_DOCUMENTS:
        draft.documents = payload ?? null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.GET_HISTORY:
        if (!checkIsSettledForRequestedSubCompany(draft, meta)) {
          break;
        }

        draft.history = payload || [];
        draft.ownerSubCompanyId = meta?.subCompanyId ?? null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.GET_CAN_START_NEW_CYCLE:
        draft.canStartNewCycle = payload ?? null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.GET_PROFILE:
        draft.profile = payload || null;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.UPDATE_PROFILE_SECTION:
        if (draft.profile && payload?.section) {
          draft.profile[payload.section] = payload.data;
        }

        break;

      case TPO_CERTIFICATION_ACTION_TYPE.APPROVE:
      case TPO_CERTIFICATION_ACTION_TYPE.REJECT:
      case TPO_CERTIFICATION_ACTION_TYPE.REVOKE:
      case TPO_CERTIFICATION_ACTION_TYPE.START_NEW_CYCLE:
        draft.cert = payload || draft.cert;
        break;

      case TPO_CERTIFICATION_ACTION_TYPE.RESET_STATE:
      case RESET_STATE:
        return initialState;

      default:
        return state;
    }
  });

export default tpoCertificationReducer;
